import React, { useEffect, useState, useCallback, useMemo } from 'react';
import AccordionItem from './AccordionItem';
import { getTenderSubChapters } from 'src/services/requests/tenders/tenders';
import { Spinner } from '../spinner/Spinner';

const CollapsibleTree = ({
  t,
  tenderStatus,
  data,
  hasAccordionItemsVisible,
  hasContentToDoubleCheckItemsVisible,
  hasSpecialElementsVisible,
  hgcRelevantContentVisible,
  setActiveChapter
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [tenderSubChapters, setTenderSubChapters] = useState([]);
  const [hasContentToDoubleCheckItems, setHasContentToDoubleCheckItems] = useState([]);
  const [hasSpecialElementItems, setHasSpecialElementItems] = useState([]);
  const [hgcRelevantContentItems, setHgcRelevantContentItems] = useState([]);
  const [hgcRelevantHasContentToDoubleCheckItems, setHgcRelevantHasContentToDoubleCheckItems] = useState([]);
  const [hgcRelevantHasSpecialElementItems, setHgcRelevantHasSpecialElementItems] = useState([]);

  const fetchTenderSubChapters = useCallback((id) => {
    setIsLoading(true);
    getTenderSubChapters(id).then((result) => {
      setTenderSubChapters(result.data || []);
      setIsLoading(false);
    });
  }, []);

  useEffect(() => {
    if (data?.id && tenderSubChapters.length === 0) {
      fetchTenderSubChapters(data.id);
    }
  }, [data]);

  useEffect(() => {
    if (data?.id) {
      setActiveChapter(data.id);
    }
  }, [data?.id, setActiveChapter]);

  const filterItems = useCallback((items) => {
    const relevantContentItems = new Set();
    const contentToDoubleCheckItems = new Set();
    const specialElementItems = new Set();

    items.forEach((item) => {
      const selectedContacts = item.suppliers?.reduce((total, supplier) =>
        total + supplier.contacts.filter(contact => contact.isSelected).length, 0) || 0;
      const hasSpecialElement = item.elements?.some(element => element.isR) || item.isR || false;

      if (item.suppliers?.length) relevantContentItems.add(item);
      if (selectedContacts <= 2) contentToDoubleCheckItems.add(item);
      if (hasSpecialElement) specialElementItems.add(item);
    });

    return {
      relevantContentItems: Array.from(relevantContentItems),
      contentToDoubleCheckItems: Array.from(contentToDoubleCheckItems),
      specialElementItems: Array.from(specialElementItems),
    };
  }, []);

  useEffect(() => {
    const { relevantContentItems, contentToDoubleCheckItems, specialElementItems } = filterItems(tenderSubChapters);
    setHgcRelevantContentItems(relevantContentItems);
    setHasContentToDoubleCheckItems(contentToDoubleCheckItems);
    setHasSpecialElementItems(specialElementItems);
  }, [tenderSubChapters, filterItems]);

  useEffect(() => {
    const contentToDoubleCheck = new Set();
    const hasSpecialElement = new Set();

    hgcRelevantContentItems.forEach((item) => {
      const selectedContacts = item.suppliers?.reduce((total, supplier) =>
        total + supplier.contacts.filter(contact => contact.isSelected).length, 0) || 0;
      const hasSpecial = item.elements?.some(element => element.isR) || false;

      if (selectedContacts <= 2) contentToDoubleCheck.add(item);
      if (hasSpecial) hasSpecialElement.add(item);
    });

    setHgcRelevantHasContentToDoubleCheckItems(Array.from(contentToDoubleCheck));
    setHgcRelevantHasSpecialElementItems(Array.from(hasSpecialElement));
  }, [hgcRelevantContentItems]);

  useEffect(() => {
    if(data?.id && tenderSubChapters.length !== 0)
      fetchTenderSubChapters(data?.id)
  }, [hasContentToDoubleCheckItemsVisible])

  const itemsToRender = useMemo(() => {
    if (hasSpecialElementsVisible && !hgcRelevantContentVisible) {
      return hasSpecialElementItems;
    }
    if (hasSpecialElementsVisible && hgcRelevantContentVisible) {
      return hgcRelevantHasSpecialElementItems;
    }
    if (hasContentToDoubleCheckItemsVisible && !hgcRelevantContentVisible) {
      return hasContentToDoubleCheckItems;
    }
    if (hasContentToDoubleCheckItemsVisible && hgcRelevantContentVisible) {
      return hgcRelevantHasContentToDoubleCheckItems;
    }
    if (hgcRelevantContentVisible) {
      return hgcRelevantContentItems;
    }
    return tenderSubChapters;
  }, [
    hasSpecialElementsVisible,
    hgcRelevantContentVisible,
    hasContentToDoubleCheckItemsVisible,
    hasSpecialElementItems,
    hgcRelevantHasSpecialElementItems,
    hasContentToDoubleCheckItems,
    hgcRelevantHasContentToDoubleCheckItems,
    hgcRelevantContentItems,
    tenderSubChapters
  ]);

  return (
    <div className='accordion-item-wrapper'>
      <div className='accordion-item-header'>
        <h4 className='accordion-wrapper-title'>{data.content}</h4>
      </div>
      <div className='main-wrapper dropdown'>
        <Spinner pageLoading={isLoading} className='p-4'>
          <div className='accordion'>
            {itemsToRender.sort((a, b) => (a.npkNumber > b.npkNumber ? 1 : -1)).map((chapter) => (
              <AccordionItem
                key={chapter.id}
                hasAccordionItemsVisible={hasAccordionItemsVisible}
                fetchTenderSubChapters={fetchTenderSubChapters}
                tenderStatus={tenderStatus}
                tenderChapter={data.id}
                t={t}
                data={chapter}
              />
            ))}
          </div>
        </Spinner>
      </div>
    </div>
  );
};

export default CollapsibleTree;
